<style scoped>
.ia-topbar {
	display: flex;
	align-items: center;
	gap: 12px;
	height: 48px;
	padding: 0 16px;
	background: #fff;
	border-bottom: 1px solid #dee2e6;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
</style>

<template>
	<FullLayout>
		<template #topbar>
			<div class="ia-topbar">
				<RouterLink
					to="/_/intelligence"
					class="btn btn-sm btn-outline-secondary"
				>
					<i class="bi bi-arrow-left me-1"></i>Intelligence
				</RouterLink>
				<span class="fw-semibold">
					<i class="bi bi-binoculars me-2"></i>Invasive Aedes
				</span>
				<span class="text-muted small">
					Placer MVCD · {{ REPORT_PERIOD }} · {{ SITES.length }} sites ·
					{{ TOTAL_FINDINGS }} larval findings
				</span>
				<span class="badge bg-warning text-dark ms-auto">Mock data</span>
			</div>
		</template>

		<InvasiveAedesMap
			:selected-site-id="selectedSiteId"
			:location="location"
			@select-site="selectSite"
			@select-location="selectLocation"
		/>

		<ToolRail
			side="left"
			v-model="leftTab"
			:tabs="[{ key: 'sites', icon: 'bi bi-house', title: 'Found sites' }]"
			topOffset="60px"
		>
			<template #sites>
				<InvasiveAedesSiteList
					:sites="SITES"
					:selected-site-id="selectedSiteId"
					@select-site="selectSite"
				/>
			</template>
		</ToolRail>

		<ToolRail
			side="right"
			v-model="rightTab"
			:tabs="[{ key: 'traps', icon: 'bi bi-bar-chart', title: 'Trap counts' }]"
			topOffset="60px"
			bottomOffset="12px"
		>
			<template #traps>
				<InvasiveAedesTrapChart
					:location="location"
					:location-label="locationLabel"
				/>
			</template>
		</ToolRail>
	</FullLayout>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { RouterLink } from "vue-router";

import FullLayout from "@/components/layout/Full.vue";
import ToolRail from "@/components/panel/ToolRail.vue";
import InvasiveAedesMap from "@/components/InvasiveAedesMap.vue";
import InvasiveAedesSiteList from "@/components/InvasiveAedesSiteList.vue";
import InvasiveAedesTrapChart from "@/components/InvasiveAedesTrapChart.vue";
import {
	DEFAULT_LOCATION,
	DEFAULT_LOCATION_LABEL,
	HEX_CLUSTERS,
	REPORT_PERIOD,
	SITES,
	TOTAL_FINDINGS,
	distanceKm,
} from "@/mock/invasive-aedes";

// Floating panels
const leftTab = ref<string | null>("sites");
const rightTab = ref<string | null>("traps");

// The "current location" the trap-count panel reads from: a site picked in the
// list, a hex clicked on the map, or the district default on load.
const selectedSiteId = ref<string | null>(null);
const location = ref({ ...DEFAULT_LOCATION });
const locationLabel = ref(DEFAULT_LOCATION_LABEL);

function selectSite(siteId: string) {
	const site = SITES.find((s) => s.id === siteId);
	if (!site) return;
	selectedSiteId.value = site.id;
	location.value = { latitude: site.latitude, longitude: site.longitude };
	locationLabel.value = site.name;
}

function selectLocation(latitude: number, longitude: number) {
	selectedSiteId.value = null;
	location.value = { latitude, longitude };
	const cluster = [...HEX_CLUSTERS].sort(
		(a, b) =>
			distanceKm(latitude, longitude, a.latitude, a.longitude) -
			distanceKm(latitude, longitude, b.latitude, b.longitude),
	)[0];
	locationLabel.value = `near ${cluster.name}`;
}
</script>
